import React, { useState, useEffect } from 'react';
import { getPools, getPoolEntries, getGames } from '../services/api';
import { usePickState }   from '../hooks/usePickState';
import PickGroupList      from '../components/PickGroupList';
import TiebreakerCard     from '../components/TiebreakerCard';

function AdminPickEntry({ showFeedback, activeTournament }) {
    const [pools,        setPools]        = useState([]);
    const [localPool,    setLocalPool]    = useState(null);
    const [entries,      setEntries]      = useState([]);
    const [activeEntry,  setActiveEntry]  = useState(null);
    const [games,        setGames]        = useState([]);

    useEffect(() => {
        if (!activeTournament) return;
        getPools(activeTournament.id).then(res => {
            setPools(res.data);
            if (res.data.length > 0) setLocalPool(res.data[0]);
            else setLocalPool(null);
        });
        getGames(activeTournament.id, null).then(res => setGames(res.data));
    }, [activeTournament]);

    // When the pool changes, reset the entry list and clear the active
    // participant. usePickState's own effect clears the picks map.
    useEffect(() => {
        if (!localPool) return;
        getPoolEntries(localPool.id).then(res => {
            setEntries(res.data);
            setActiveEntry(null);
        });
    }, [localPool]);

    const {
        picks,
        gameMap,
        groups,
        activeGroup,
        setActiveGroup,
        collapsedRounds,
        toggleCollapse,
        saving,
        handlePick,
        counts,
    } = usePickState({
        activeEntry,
        games,
        poolType:                    localPool?.pool_type,
        onFeedback:                  showFeedback,
        autoOpenFirstScheduledGroup: false,
        collapseSingleRoundGroups:   true,
    });

    const { pickedCount, totalInGroup, isGroupLocked } = counts;
    const totalScheduled = isGroupLocked ? 0 : totalInGroup;

    // Identify the championship game (highest-numbered round's single
    // game) from the games payload. Reused for both the lock state
    // and the input labels in TiebreakerCard.
    const championshipGame = games.length > 0
        ? games.find(g => g.round_number === Math.max(...games.map(x => x.round_number)))
        : null;
    const championshipFinal = championshipGame?.status === 'final';

    // After a tiebreaker save, update both the active entry and its
    // row in the entries list so the saved values persist across
    // participant switches without a re-fetch.
    const handleTiebreakerSaved = (savedRow) => {
        setActiveEntry(prev => prev && prev.entry_id === savedRow.id
            ? { ...prev,
                tiebreaker_team1_score: savedRow.tiebreaker_team1_score,
                tiebreaker_team2_score: savedRow.tiebreaker_team2_score }
            : prev);
        setEntries(prev => prev.map(e =>
            e.entry_id === savedRow.id
                ? { ...e,
                    tiebreaker_team1_score: savedRow.tiebreaker_team1_score,
                    tiebreaker_team2_score: savedRow.tiebreaker_team2_score }
                : e));
    };

    return (
        <div>
            <div className="flex gap-3 mb-4 flex-wrap items-end">
                <div className="flex flex-col gap-1">
                    <span className="text-xs text-gray-500 uppercase tracking-wide">Pool</span>
                    <select value={localPool?.id || ''}
                        onChange={e => setLocalPool(pools.find(p => p.id === parseInt(e.target.value)))}
                        className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500">
                        {pools.length === 0 && <option value="">No pools</option>}
                        {pools.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                </div>
            </div>

            <div className="sticky top-24 z-40 bg-gray-950 pb-2">
                <div className="flex flex-wrap gap-2 mb-3">
                    {entries.map(e => (
                        <button key={e.entry_id} onClick={() => setActiveEntry(e)}
                            className={'px-4 py-2 rounded-lg text-sm font-bold transition-colors ' +
                                (activeEntry?.entry_id === e.entry_id
                                    ? 'bg-orange-500 text-white'
                                    : 'bg-gray-800 text-gray-400 hover:text-white')}>
                            {e.participant}
                        </button>
                    ))}
                </div>

                {activeEntry && totalScheduled > 0 && (
                    <div className={'px-5 py-3 rounded-xl mb-3 border text-sm font-medium ' +
                        (pickedCount === totalScheduled
                            ? 'bg-green-900/30 border-green-700 text-green-300'
                            : 'bg-gray-900 border-gray-700 text-gray-400')}>
                        {pickedCount === totalScheduled
                            ? `All ${totalScheduled} picks entered for ${activeEntry.participant}`
                            : `${pickedCount} of ${totalScheduled} picks entered for ${activeEntry.participant}`}
                    </div>
                )}

                {activeEntry && localPool?.use_tiebreaker && (
                    <TiebreakerCard
                        entry={activeEntry}
                        championshipGame={championshipGame}
                        championshipFinal={championshipFinal}
                        onSaved={handleTiebreakerSaved}
                        onFeedback={showFeedback}
                    />
                )}

                {activeEntry && (
                    <div className="flex gap-1 border-b border-gray-800 overflow-x-auto">
                        {groups.map(g => {
                            const gGames    = g.rounds.flatMap(r => r.games);
                            const scheduled = gGames.filter(x => x.status === 'scheduled');
                            // Tab badge counts picks over ALL games in the group
                            // (final + scheduled) so missed picks surface to the
                            // admin at the tab level.
                            const picked    = gGames.filter(x => picks[x.id]).length;
                            const allFinal  = gGames.every(x => x.status === 'final');
                            const locked    = scheduled.length > 0 && scheduled.every(x => !x.team1_id && !x.team2_id);
                            return (
                                <button key={g.key} onClick={() => setActiveGroup(g)}
                                    className={'px-4 py-2 text-sm font-bold rounded-t-lg transition-colors whitespace-nowrap ' +
                                        (activeGroup?.key === g.key
                                            ? 'bg-gray-950 text-orange-400 border-t border-x border-gray-800'
                                            : allFinal
                                                ? 'text-gray-600'
                                                : 'text-gray-400 hover:text-white')}>
                                    {g.label}
                                    {allFinal && <span className="ml-1 text-gray-600 text-xs">✓</span>}
                                    {locked && <span className="ml-1 text-gray-600 text-xs">🔒</span>}
                                    {!allFinal && !locked && gGames.length > 0 && (
                                        <span className={'ml-1 text-xs ' + (picked === gGames.length ? 'text-green-400' : 'text-orange-400')}>
                                            {picked}/{gGames.length}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            {!activeEntry && (
                <div className="text-gray-500 text-center py-12">
                    Select a participant above to enter their picks.
                </div>
            )}

            {activeEntry && (
                <div className="mt-4">
                    <PickGroupList
                        activeGroup={activeGroup}
                        isGroupLocked={isGroupLocked}
                        picks={picks}
                        gameMap={gameMap}
                        saving={saving}
                        collapsedRounds={collapsedRounds}
                        onToggleCollapse={toggleCollapse}
                        onPick={handlePick}
                        collapsibleRule="always"
                        regionDisplayNames={activeTournament?.region_display_names}
                    />
                </div>
            )}
        </div>
    );
}

export default AdminPickEntry;